import React from 'react';
import {
  CheckCircle2,
  CircleDashed,
  Clock3,
  RefreshCcw,
  ShieldAlert,
  TriangleAlert,
  XCircle,
} from 'lucide-react';

import {
  lifecyclePresentation,
  type PresentableLifecycle,
} from '../../lib/decisionAnalysis/lifecyclePresentation';
import { StatusPill, type StatusPillVariant } from '../ui/status-pill';

/**
 * Canonical status-pill variant for an analysis lifecycle.
 *
 * This is the Findings matrix mapping. Path to GO consumes the same helper so
 * the same analysis cannot change colour when a user switches tabs.
 */
export function analysisLifecycleStatusVariant(
  lifecycle: PresentableLifecycle,
  truthQuestionCount = 0,
  autoRefreshExhausted = false,
): StatusPillVariant {
  const presentation = lifecyclePresentation(
    lifecycle,
    truthQuestionCount,
    autoRefreshExhausted,
  );
  if (presentation.held) return 'warn';
  if (lifecycle === 'current') return 'success';
  if (lifecycle === 'available' || lifecycle === 'ready' || lifecycle === 'preview') {
    return 'custom';
  }
  if (lifecycle === 'failed' || lifecycle === 'failed-coverage' || lifecycle === 'unavailable') return 'danger';
  if (lifecycle === 'queued' || lifecycle === 'generating') return 'neutral';
  return 'warn';
  }

  export function AnalysisLifecycleIcon({
  lifecycle,
  }: {
  lifecycle: PresentableLifecycle;
  }): React.ReactElement {
  const className = 'h-3 w-3';
  if (lifecycle === 'current') {
    return <CheckCircle2 className={className} aria-hidden />;
  }
  if (lifecycle === 'queued' || lifecycle === 'generating') {
    return <CircleDashed className={`${className} animate-spin`} aria-hidden />;
  }
  if (lifecycle === 'failed' || lifecycle === 'failed-coverage' || lifecycle === 'unavailable') {
    return <XCircle className={className} aria-hidden />;
  }
  if (lifecycle === 'refresh-needed') {
    return <RefreshCcw className={className} aria-hidden />;
  }
  if (lifecycle === 'quality-held') {
    return <ShieldAlert className={className} aria-hidden />;
  }
  return <TriangleAlert className={className} aria-hidden />;
}

export interface AnalysisLifecycleStatusPillProps {
  lifecycle: PresentableLifecycle;
  truthQuestionCount?: number;
  autoRefreshExhausted?: boolean;
  /** Live queued/generating stage or countdown; lifecycle presentation stays canonical. */
  labelOverride?: React.ReactNode;
  'data-testid'?: string;
}

/** One lifecycle badge shared by Findings and Path to GO > Analyse. */
export const AnalysisLifecycleStatusPill: React.FC<AnalysisLifecycleStatusPillProps> = ({
  lifecycle,
  truthQuestionCount = 0,
  autoRefreshExhausted = false,
  labelOverride,
  'data-testid': testId,
}) => {
  const presentation = lifecyclePresentation(
    lifecycle,
    truthQuestionCount,
    autoRefreshExhausted,
  );
  const iconLifecycle = presentation.held ? 'quality-held' : lifecycle;
  const label = labelOverride ?? presentation.label;
  // A lifecycle without an on-screen badge (quality-held) renders nothing.
  if (label == null) return null;

  return (
    <StatusPill
      variant={analysisLifecycleStatusVariant(
        lifecycle,
        truthQuestionCount,
        autoRefreshExhausted,
      )}
      data-testid={testId}
    >
      <AnalysisLifecycleIcon lifecycle={iconLifecycle} />
      {label}
    </StatusPill>
  );
};

export default AnalysisLifecycleStatusPill;
